import { BASE_URL } from 'utils/env';
import { hasValue } from 'utils/values';

import { type OutputOperationResult } from './types';

/** fetch with the access token. */
export const fetchWithToken = <T = string>(
  url: string,
  token: string,
  params?: RequestInit
): Promise<OutputOperationResult<T>> => {
  const { headers, ...otherParams } = params || {};
  return fetcher(url, { headers: { ...headers, Authorization: 'Bearer ' + token }, ...otherParams });
};

/**
 * A custom implementation using the browser's native fetch API with the default content-type of application/json.
 */
export async function fetcher<T = void>(url: string, params?: RequestInit): Promise<OutputOperationResult<T>> {
  const targetUrl = getFullUrl(url);
  const { headers, body, ...otherParams } = params || {};

  // Respect the specified content-type if it's already set in the headers.
  const specifiedContentType = headers instanceof Headers ? headers.get('Content-Type') : null;
  // Intentionally leave out Content-Type for FormData and let the browser handle it.
  // If not provided, default to application/json.
  const contentType = body instanceof FormData ? undefined : specifiedContentType || 'application/json';

  const updatedHeaders = contentType ? { ...headers, 'Content-Type': contentType } : headers;

  try {
    const response = await fetch(targetUrl, { headers: updatedHeaders, body, ...otherParams });

    if (isJsonResponse(response)) {
      return processJsonResponse<T>(response);
    } else {
      return processTextResponse<T>(response);
    }
  } catch (error) {
    const message = handleGenericError(error);
    return { success: false, error: message };
  }
}

const handleGenericError = (error: unknown) => {
  if (error instanceof Error) {
    const message = error.message.includes('Failed to fetch') ? 'Network error or CORS issue' : error.message;

    return 'An error occurred: ' + message;
  } else if (typeof error === 'string') {
    return 'An error occurred: ' + error;
  } else {
    return 'An unknown error occurred: ' + JSON.stringify(error);
  }
};

export type Fetcher = typeof fetcher;

const getFullUrl = (url: string) => (url.startsWith('http') ? url : `${BASE_URL}${url}`);

const processJsonResponse = async <T>(response: Response): Promise<OutputOperationResult<T>> => {
  if (isRejected(response)) {
    const error = await getErrorMessage(response);
    return { success: false, error };
  } else {
    const jsonResponse: T = await response.json();
    // In case BE didn't wrap the response in
    // the standard OutputOperationResult<T> format, we should handle it gracefully.
    if (isOutputOperationResult<T>(jsonResponse)) {
      return jsonResponse;
    } else {
      return { success: true, result: jsonResponse };
    }
  }
};

const processTextResponse = async <T>(response: Response): Promise<OutputOperationResult<T>> => {
  const textResponse = await response.text();
  if (isRejected(response)) {
    return { success: false, error: textResponse };
  } else {
    try {
      // In case of empty response (i.e. T = void), return success with undefined result.
      if (textResponse.length === 0) {
        return { success: true, result: undefined as T };
      }
      // Note: our BE should always return JSON, but in case it doesn't,
      //  we should handle it gracefully.
      const jsonResponse: T = JSON.parse(textResponse);
      if (isOutputOperationResult<T>(jsonResponse)) {
        return jsonResponse;
      } else {
        return { success: true, result: jsonResponse };
      }
    } catch (error) {
      return { success: false, error: String(error) };
    }
  }
};

// Check if the value is an OutputOperationResult<T> by checking if it has a 'success' property.
const isOutputOperationResult = <T>(value: unknown): value is OutputOperationResult<T> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return hasValue((value as any)?.success);
};

const isRejected = (response: Response) => !response.ok || response.status >= 299;

/**
 * Get an error message from a JSON response.
 *
 * The backend answers failures with RFC 9110 problem details (`title`/`detail`) or with our own
 * `{ success: false, error }` envelope — neither of which carries a `message`. Reading only `message`
 * therefore threw away every server-supplied reason and left callers with a bare "400" to show the
 * user, so check the shapes the API actually returns before falling back to the status.
 */
const getErrorMessage = async (response: Response) => {
  const responseStatus = formatStatus(response);

  const body = await response.json().catch(() => undefined);
  const detail = firstString(body?.message, body?.detail, body?.error, body?.title);

  return detail ? `${responseStatus}: ${detail}` : responseStatus;
};

const firstString = (...candidates: unknown[]) =>
  candidates.find((candidate): candidate is string => typeof candidate === 'string' && candidate.trim().length > 0);

const formatStatus = (response: Response) =>
  response.statusText ? `${response.status} (${response.statusText})` : response.status.toString();

const isJsonResponse = (response: Response) => {
  const contentType = response.headers.get('content-type');
  // The API serves RFC 9110 problem details as `application/problem+json`. Matching only
  // `application/json` sent those down the plain-text path, which returns the response body verbatim —
  // which is how a 404 ended up rendering the raw `{"type":...,"traceId":...}` payload at the user.
  return hasValue(contentType) && /^application\/(problem\+)?json/.test(contentType);
};
